import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { openPrintCardOrderItemPreviewDialog } from "../../print-card-orders/print-card-order-item-preview-dialog/print-card-order-item-preview.dialog";
import {
  FULFILLMENT_BLOCKED_REASON,
  OrderItemsGroupView,
  OrderItemsListItem,
  OrderItemsListRecipient,
  OrderItemsSummary,
} from "../card-orders.interface";
import {
  buildOrderItemsGroups,
  getOrderItemSizeLabel,
  getOrderItemsListSummary,
  getTemplateTypeLabelKey,
} from "../card-order.util";

@Component({
  selector: "app-order-items-list",
  templateUrl: "./order-items-list.component.html",
  styleUrls: ["./order-items-list.component.scss"],
  standalone: false,
})
export class OrderItemsListComponent implements OnChanges {
  protected readonly fulfillmentBlockedReason = FULFILLMENT_BLOCKED_REASON;

  @Input() recipients: OrderItemsListRecipient[] = [];
  @Input() showPrinterDetails = false;
  @Input() showPreviewActions = false;
  @Input() showNameLabelActions = false;
  @Input() nameLabelLoading: string | null = null;
  @Input() orderId: string | null = null;
  @Input() previewApi: "admin" | "tenant" = "tenant";
  @Output() nameLabelPrint = new EventEmitter<{
    employeeEmail: string;
    withQr: boolean;
  }>();

  groups: OrderItemsGroupView[] = [];
  summary: OrderItemsSummary = {
    recipientCount: 0,
    lineCount: 0,
    totalQuantity: 0,
  };
  showGroupHeaders = false;
  showPrinterOrderColumn = false;
  showPrinterColumns = false;
  showPrinterNameColumn = false;
  printerParameterColumns: string[] = [];
  columnCount = 6;
  leadingColumnCount = 2;

  constructor(
    private readonly translate: TranslateService,
    private readonly dialog: MatDialog,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes["recipients"] ||
      changes["showPrinterDetails"] ||
      changes["showPreviewActions"] ||
      changes["showNameLabelActions"] ||
      changes["orderId"]
    ) {
      this.refreshView();
    }
  }

  openPreview(
    group: OrderItemsGroupView,
    row: { item: OrderItemsListItem },
    event?: Event,
  ): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.canPreviewItem(group, row)) {
      return;
    }

    const orderId = this.orderId?.trim();
    const itemId = row.item.id?.trim();
    if (orderId) {
      if (!itemId) {
        return;
      }
      openPrintCardOrderItemPreviewDialog(this.dialog, {
        mode: "order",
        orderId,
        previewApi: this.previewApi,
        itemId,
        hasPdf: row.item.hasPdf,
        title: row.item.templateName,
        subtitle: group.employeeName,
      });
      return;
    }

    const templateId = row.item.templateId?.trim();
    const employeeEmail = group.employeeEmail?.trim();
    if (!templateId || !employeeEmail) {
      return;
    }

    openPrintCardOrderItemPreviewDialog(this.dialog, {
      mode: "draft",
      templateId,
      employeeEmail,
      pageSizePresetId: row.item.pageSizePresetId,
      templateType: row.item.templateType,
      orientation: row.item.orientation,
      title: row.item.templateName,
      subtitle: group.employeeName,
    });
  }

  canPreviewItem(
    group: OrderItemsGroupView,
    row: { item: OrderItemsListItem },
  ): boolean {
    if (!this.showPreviewActions) {
      return false;
    }
    if (this.orderId?.trim()) {
      return Boolean(row.item.id?.trim());
    }
    return Boolean(row.item.templateId?.trim() && group.employeeEmail?.trim());
  }

  getSizeLabel(item: OrderItemsListItem): string {
    return getOrderItemSizeLabel(
      item,
      (key) => this.translate.instant(key) as string,
    );
  }

  getOrientationLabelKey(item: OrderItemsListItem): string {
    return item.orientation === "portrait"
      ? "printCards.orientation.portrait"
      : "printCards.orientation.landscape";
  }

  getTypeLabelKey(item: OrderItemsListItem): string {
    return getTemplateTypeLabelKey(item.templateType);
  }

  getPrinterOrderNumber(item: OrderItemsListItem): string {
    return (
      item.printerOrderNumber?.trim() || item.printer?.orderNumber?.trim() || ""
    );
  }

  getPrinterStatusLabel(item: OrderItemsListItem): string {
    return (
      item.printerStatusLabel?.trim() || item.printer?.statusLabel?.trim() || ""
    );
  }

  printerParameterValue(item: OrderItemsListItem, labelKey: string): string {
    const param = item.printerParameters?.find(
      (entry) => entry.labelKey === labelKey,
    );
    if (!param) {
      return "";
    }
    if (param.valueKey) {
      return this.translate.instant(param.valueKey) as string;
    }
    return param.value?.trim() || "";
  }

  private refreshView(): void {
    const recipients = this.recipients ?? [];
    this.groups = buildOrderItemsGroups(recipients);
    this.summary = getOrderItemsListSummary(recipients);
    this.showGroupHeaders =
      this.showNameLabelActions ||
      recipients.length > 1 ||
      recipients.some(
        (recipient) =>
          recipient.fulfillmentBlockedReason ===
          FULFILLMENT_BLOCKED_REASON.EMPLOYEE_DELETED,
      );

    const allRows = this.groups.flatMap((group) => group.rows);
    this.showPrinterColumns =
      this.showPrinterDetails &&
      allRows.some(
        (row) =>
          Boolean(row.item.printerOrderNumber?.trim()) ||
          Boolean(row.item.printer?.orderNumber?.trim()) ||
          Boolean(row.item.printerStatusLabel?.trim()) ||
          Boolean(row.item.printer?.statusLabel?.trim()),
      );
    this.showPrinterOrderColumn =
      this.showPrinterColumns || this.showPreviewActions;
    this.showPrinterNameColumn =
      this.showPrinterDetails &&
      allRows.some((row) => Boolean(row.item.printerName));

    const parameterColumns: string[] = [];
    if (this.showPrinterDetails) {
      const seenParameterColumns = new Set<string>();
      for (const row of allRows) {
        for (const param of row.item.printerParameters ?? []) {
          if (
            seenParameterColumns.has(param.labelKey) ||
            (!param.valueKey?.trim() && !param.value?.trim())
          ) {
            continue;
          }
          seenParameterColumns.add(param.labelKey);
          parameterColumns.push(param.labelKey);
        }
      }
    }
    this.printerParameterColumns = parameterColumns;

    this.leadingColumnCount = 2 + (this.showPrinterOrderColumn ? 1 : 0);
    this.columnCount =
      this.leadingColumnCount +
      4 +
      (this.showPrinterColumns ? 1 : 0) +
      (this.showPrinterNameColumn ? 1 : 0) +
      this.printerParameterColumns.length;
  }

  get trailingColumnCount(): number {
    return this.columnCount - this.leadingColumnCount;
  }
}
